import { useCallback, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

type Value = string | number | boolean | string[];
type Widen<T> = { [K in keyof T]: T[K] extends boolean ? boolean : T[K] };

const read = <T extends Record<string, Value>>(defaults: T, params: URLSearchParams) =>
  Object.fromEntries(
    Object.entries(defaults).map(([key, fallback]) => {
      const raw = params.get(key);
      if (Array.isArray(fallback)) return [key, params.has(key) ? params.getAll(key) : fallback];
      if (raw === null) return [key, fallback];
      if (typeof fallback === "number") return [key, Number.isInteger(Number(raw)) ? Number(raw) : fallback];
      if (typeof fallback === "boolean") return [key, raw === "true"];
      return [key, raw];
    })
  ) as Widen<T>;

const write = <T extends Record<string, Value>>(defaults: T, search: string, changes: Partial<Widen<T>>) => {
  const params = new URLSearchParams(search);
  Object.entries(changes).forEach(([key, value]) => {
    params.delete(key);
    if (value !== undefined && JSON.stringify(value) !== JSON.stringify(defaults[key])) {
      [value].flat().forEach(v => params.append(key, String(v)));
    }
  });
  return params.toString();
};

export const useViewState = <T extends Record<string, Value>>(defaults: T, { inUrl = true } = {}) => {
  const history = useHistory();
  const location = useLocation();
  const [local, setLocal] = useState("");
  const defaultsRef = useRef(defaults);
  const view = read(defaults, new URLSearchParams(inUrl ? location.search : local));

  const set = useCallback(
    (changes: Partial<Widen<T>>) =>
      inUrl
        ? history.replace({ ...history.location, search: write(defaultsRef.current, history.location.search, changes) })
        : setLocal(current => write(defaultsRef.current, current, changes)),
    [inUrl, history]
  );

  return [view, set] as const;
};

export const oneOf = <T extends Value>(options: readonly T[], value: T, fallback: T) =>
  options.includes(value) ? value : fallback;

export const useKeepView = (drop: readonly string[]) => {
  const { search } = useLocation();
  return useCallback(
    (pathname: string) => {
      const params = new URLSearchParams(search);
      drop.forEach(key => params.delete(key));
      const kept = params.toString();
      return kept ? `${pathname}?${kept}` : pathname;
    },
    [search, drop]
  );
};
